
import type { VNode } from 'vue';
import type { WithRefs } from 'vue-typed-refs';
import type { WithProperties } from 'vue-typed-properties';
import { Wormhole } from 'portal-vue';
import { nanoid } from 'nanoid';
import invariant from 'invariant';

import type { Undefined, Nullable } from '@/types';

import { PopperMixin } from '../../mixins';

type Refs = {
  tooltip: HTMLDivElement;
};

export default (
  PopperMixin as WithRefs<
    Refs,
    WithProperties<{ timeout: Undefined<number> }, typeof PopperMixin>
  >
).extend({
  inheritAttrs: false,
  props: {
    uid: {
      type: String,
      default() {
        return nanoid();
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    portalFrom(): string {
      return `Tooltip-${this.uid}`;
    },
    activatorAttrs(): Record<string, string | number | boolean> {
      return {
        'aria-describedby': this.rootId,
      };
    },
    activatorOn(): Record<string, () => void> {
      return {
        mouseenter: this.onMouseEnter,
        mouseleave: this.onMouseLave,
        focus: this.show,
        blur: this.hide,
      };
    },
    rootId() {
      return `tooltip-${this.uid}`;
    },
  },
  watch: {
    isOpen(value: boolean) {
      if (value) {
        const res = this.updateWormhole();
        if (res) {
          this.$nextTick(() => {
            this.createPopperInstance(this.$el, this.$refs.tooltip);
            this.$emit('open');
          });
        }
      } else {
        this.destroy();
      }
    },
  },
  created() {
    this.timeout = undefined;
  },
  /**
   * That's crucial. Please, don't remove it.
   * Ensures that Wormhole always contains a fresh content.
   */
  updated() {
    if (this.isOpen) {
      this.updateWormhole();
    }
  },
  beforeDestroy() {
    this.destroy();
  },
  methods: {
    destroy() {
      if (this.timeout !== undefined) {
        window.clearTimeout(this.timeout);
      }
      this.destroyPopperInstance();
      Wormhole.close({
        to: 'tooltip',
        from: this.portalFrom,
      });
      this.$emit('close');
    },
    show() {
      if (this.disabled === false) {
        this.isOpen = true;
      }
    },
    hide() {
      this.isOpen = false;
    },
    renderTooltip(): Nullable<VNode> {
      if (this.$slots.default === undefined) return null;
      const h = this.$createElement;

      return h(
        'div',
        {
          ref: 'tooltip',
          attrs: {
            id: this.rootId,
            role: 'tooltip',
            ...this.$attrs,
          },
          staticClass: 'slds-popover slds-popover_tooltip',
          class: [`slds-nubbin_${this.getNubbin()}`],
        },
        [
          h(
            'div',
            {
              staticClass: 'slds-popover__body',
            },
            this.$slots.default
          ),
        ]
      );
    },
    updateWormhole() {
      const tooltip = this.renderTooltip();

      if (tooltip === null) {
        this.hide();
        return false;
      }

      Wormhole.open({
        to: 'tooltip',
        from: this.portalFrom,
        passengers: [tooltip],
      });

      return true;
    },
    onMouseEnter() {
      this.timeout = window.setTimeout(() => {
        this.show();
      }, 100);
    },
    onMouseLave() {
      if (this.timeout !== undefined) {
        window.clearTimeout(this.timeout);
      }
      this.hide();
    },
  },
  render(): VNode {
    const { activator } = this.$scopedSlots;
    const message = "`'activator'` slot is not provided";
    invariant(activator, message);

    const content = activator({
      attrs: this.activatorAttrs,
      on: this.activatorOn,
      isOpen: this.isOpen,
    });

    invariant(content, message);

    return content[0];
  },
});
